import React from 'react';
import Typography from '@ntuctech/devex-tangram/Typography';
import styled from 'styled-components';

import { FLAGS } from '../../services/feature-flag';
import { formatPhoneNumber } from '../../services/utils';
import { useFeatures } from '../DynamicFeatureProvider';
import EditAddressForm from '../EditAddressForm';
import EditNameForm from '../EditNameForm';
import HocProfileManager from '../HocProfileManager';
import ProfileAction from '../ProfileAction';
import ProfileContent, { ProfileContentRow } from '../ProfileContent';
import ProfileRow from '../ProfileRow';
import ProfileTitle from '../ProfileTitle';
import { InactiveLabel, VerifiedBadge } from '../Theme';

const PROFILE_DATA_LABEL = {
  name: 'Preferred name',
  dateOfBirth: 'Date of birth',
  settings: 'Settings',
};

const TypographyEmailSc = styled(Typography)`
  max-width: 19rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 767px) {
    max-width: 10rem;
  }
`;

const MobileContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media (max-width: 845px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const RenderPhoneContent = ({ phoneVerifiedProp }) => {
  return phoneVerifiedProp ? (
    <VerifiedBadge data-testid="badgePhoneVerified" color="#2070EF" />
  ) : null;
};

const RenderEmailContent = ({ emailVerifiedProp }) => {
  return emailVerifiedProp ? (
    <VerifiedBadge data-testid="badgeEmailVerified" color="#2070EF" />
  ) : null;
};

const renderEditButtonText = (isMethodVerified, isNewProfilePageEnabledProp) => {
  if (isNewProfilePageEnabledProp) {
    return isMethodVerified ? 'Edit' : 'Edit and verify';
  }
  return 'Edit';
};

const Profile = (props) => {
  const {
    // profile data
    profile,
    setLocalUser,
    // editing state & unsaved changes checking
    pageContext,
    setIsEditing,
    handleFinishEditing,
    // actions
    handleOpenForm,
    // alerts
    showSuccessToast,
    openErrorModal,
    // persona api
    isPersonaEnabled,
  } = props;

  const {
    name,
    email,
    emailVerified,
    displayPhone,
    isPhoneEmpty,
    phoneVerified,
    residentialAddress,
    isEmailSameasMyinfo,
    isMobileSameasMyinfo,
  } = profile;
  const { isFlagEnabled } = useFeatures();
  const isNewProfilePageEnabled = isFlagEnabled(FLAGS.PROFILE_VERIFICATION_REVAMP);

  const getPhone = displayPhone.split('+65')[1];
  const parsedPhone = formatPhoneNumber(getPhone);

  const showEmailHint = !emailVerified || (emailVerified && !isEmailSameasMyinfo);
  const showMobileHint = !phoneVerified || (phoneVerified && !isMobileSameasMyinfo);
  const emailHintText = isEmailSameasMyinfo
    ? 'Secure your account by verifying your email.'
    : 'This email is different from the details in your Myinfo.';
  const mobileHintText = isMobileSameasMyinfo
    ? 'Secure your account by verifying your mobile number.'
    : 'This mobile number is different from the details in your Myinfo.';

  // post-update callback actions
  const profileUpdateCallback = React.useCallback(
    (error, res, from = null) => {
      if (error) {
        openErrorModal(error);
      } else {
        showSuccessToast(`${PROFILE_DATA_LABEL[from] || 'Profile'} updated successfully`);
        setLocalUser((oldUser) => ({ ...oldUser, ...res }));
        handleFinishEditing();
      }
    },
    [openErrorModal, setLocalUser, showSuccessToast, handleFinishEditing],
  );

  return (
    <>
      {/* Name */}
      {pageContext === 'name' ? (
        <EditNameForm
          initialValues={{ name }}
          handleCallback={profileUpdateCallback}
          onClose={handleFinishEditing}
          setIsEditing={setIsEditing}
          isPersonaEnabled={isPersonaEnabled}
        />
      ) : (
        <ProfileRow>
          <ProfileTitle>{PROFILE_DATA_LABEL.name}</ProfileTitle>
          <ProfileContent className="accTextOverflow">
            <Typography
              variant="body2"
              data-testid="DisplayName"
              data-dd-action-name="Profile name"
            >
              {name}
            </Typography>
          </ProfileContent>
          <ProfileAction
            onClick={handleOpenForm('name')}
            data-testid="EditNameLink"
            className="profileButton"
          >
            Edit
          </ProfileAction>
        </ProfileRow>
      )}
      {/* Mobile number */}
      <ProfileRow>
        <ProfileTitle hasTooltip={showMobileHint} tooltipText={mobileHintText}>
          Mobile number
        </ProfileTitle>
        <ProfileContentRow>
          <MobileContentWrapper>
            <ProfileContentRow>
              <Typography
                variant="body2"
                data-testid="DisplayPhone"
                data-dd-action-name="Profile phone number"
              >
                {isPhoneEmpty ? <InactiveLabel>Not added</InactiveLabel> : parsedPhone}
              </Typography>
              <RenderPhoneContent phoneVerifiedProp={phoneVerified} />
            </ProfileContentRow>
          </MobileContentWrapper>
        </ProfileContentRow>
        <ProfileAction
          onClick={handleOpenForm('verify-mobile')}
          data-testid="EditPhoneLink"
          className="profileButton"
        >
          {renderEditButtonText(phoneVerified, isNewProfilePageEnabled)}
        </ProfileAction>
      </ProfileRow>
      {/* Email */}
      <ProfileRow>
        <ProfileTitle hasTooltip={showEmailHint} tooltipText={emailHintText}>
          Email
        </ProfileTitle>
        <ProfileContentRow>
          <TypographyEmailSc
            variant="body2"
            data-testid="DisplayEmail"
            data-dd-action-name="Profile email"
          >
            {email}
          </TypographyEmailSc>
          <RenderEmailContent emailVerifiedProp={emailVerified} />
        </ProfileContentRow>
        <ProfileAction
          onClick={handleOpenForm('verify-email')}
          data-testid="EditEmailLink"
          className="profileButton"
        >
          {renderEditButtonText(emailVerified, isNewProfilePageEnabled)}
        </ProfileAction>
      </ProfileRow>
      {/* Residential address */}
      {pageContext === 'address' ? (
        <EditAddressForm
          initialFormData={residentialAddress}
          handleCallback={profileUpdateCallback}
          closeForm={handleFinishEditing}
          mode={residentialAddress ? 'edit' : 'add'}
          isPersonaEnabled={isPersonaEnabled}
        />
      ) : (
        <ProfileRow className="alignTop">
          <ProfileTitle>Residing address</ProfileTitle>
          <ProfileContentRow className="accTextWrap">
            <Typography
              variant="body2"
              data-testid="DisplayAddress"
              data-dd-action-name="Profile address"
            >
              {residentialAddress ? (
                residentialAddress.full_address
              ) : (
                <InactiveLabel>Not added</InactiveLabel>
              )}
            </Typography>
          </ProfileContentRow>
          <ProfileAction
            onClick={handleOpenForm('address')}
            data-testid="EditAddressLink"
            className="profileButton"
          >
            {residentialAddress ? 'Edit' : 'Add'}
          </ProfileAction>
        </ProfileRow>
      )}
    </>
  );
};

export default HocProfileManager(Profile);
